import type {
  ISupplier,
  ISupplierID,
  ISupplier_Type,
} from '@bridebook/models/source/models/Suppliers.types';
import type { IUserID } from '@bridebook/models/source/models/Users.types';
import type { IWeddingID } from '@bridebook/models/source/models/Weddings.types';
import { CountryCodes } from '../gazetteer';

/**
 * Type guard to check if a value is a valid CountryCodes enum value.
 */
export function isCountryCode(value?: unknown): value is CountryCodes {
  if (typeof value !== 'string') {
    return false;
  }

  return Object.values(CountryCodes).includes(value as CountryCodes);
}

/**
 * Type guard to check if a value is defined (not null or undefined).
 * Can be used as Array.filter(isDefined) to filter out undefined elements from an array in a type safe way.
 */
export function isDefined<T>(
  value: T | null | undefined,
): value is Exclude<T, 'null' | 'undefined'> {
  return value !== null && value !== undefined;
}

/**
 * Checks if the given value is a properly formatted email address.
 */
export function isEmail(value: unknown): value is string {
  if (typeof value !== 'string') {
    return false;
  }

  return /^[0-9a-z][-_.%+0-9a-z]*@[0-9a-z][-.0-9a-z]+[.][a-z]{2,}$/i.test(value);
}

/**
 * Checks whether a given value exists within the provided set.
 *
 * - If `set` is an array, returns `true` if the array includes the value.
 * - If `set` is a `Set`, returns `true` if the set contains the value.
 * - If `set` is a single value, returns `true` if it is strictly equal to the given value.
 */
export function isInSet<T>(value: T, set: T | T[] | Set<T>): boolean {
  if (Array.isArray(set)) {
    return set.includes(value);
  }

  if (set instanceof Set) {
    return set.has(value);
  }

  return set === value;
}

/**
 * Checks if the given string is in ISO date format (YYYY-MM-DD).
 * Years 1900-2099, months 01-12 and days 01-31 are considered valid.
 * There's no validation for actual calendar dates.
 */
export function isIsoDate(value: unknown): value is string {
  if (typeof value !== 'string') {
    return false;
  }

  return /^(19|20)[0-9]{2}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/.test(value);
}

/**
 * Checks if the given string is in ISO date-time format (YYYY-MM-DDTHH:MM:SS).
 * Combines the checks for ISO date and ISO time.
 */
export function isIsoDateTime(value: unknown): value is string {
  if (typeof value !== 'string') {
    return false;
  }

  const parts = value.split('T');

  if (parts.length !== 2) {
    return false;
  }

  return isIsoDate(parts[0]) && isIsoTime(parts[1]);
}

/**
 * Checks if the given string is in ISO time format (HH:MM:SS).
 * Hours 00-23, minutes 00-59, seconds 00-59 are considered valid.
 * Optional timezone designator (Z or ±HH:MM) is also supported.
 */
export function isIsoTime(value: unknown): value is string {
  if (typeof value !== 'string') {
    return false;
  }

  return /^([01][0-9]|2[0123])(:[0-5][0-9]){2}(Z|[-+]([01][0-9]|2[0123]):([0-5][0-9]))?$/.test(
    value,
  );
}

/**
 * Type guard to check if a value is a thenable Promise.
 */
export function isPromise<T>(value: unknown): value is Promise<T> {
  if (value == null || typeof value !== 'object') {
    return false;
  }

  return 'then' in value && typeof (value as Promise<T>).then === 'function';
}

/**
 * Type guard to check if a `PromiseSettledResult` is a fulfilled result.
 */
export function isPromiseFulfilled<T>(
  value: PromiseSettledResult<T>,
): value is PromiseFulfilledResult<T> {
  if (value == null || typeof value !== 'object') {
    return false;
  }

  return 'status' in value && (value as PromiseSettledResult<T>).status === 'fulfilled';
}

/**
 * Type guard to check if a `PromiseSettledResult` is a rejected result.
 */
export function isPromiseRejected<T>(
  value: PromiseSettledResult<T>,
): value is PromiseRejectedResult {
  if (value == null || typeof value !== 'object') {
    return false;
  }

  return 'status' in value && (value as PromiseSettledResult<T>).status === 'rejected';
}

export function isString(value: unknown): value is string {
  return typeof value === 'string';
}

export function isStringNonEmpty(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Checks if the given data is a Firestore supplier.
 * Useful to differentiate between Firestore and Elastic suppliers.
 */
export function isSupplier(value: unknown): value is ISupplier {
  return Array.isArray((value as ISupplier).type);
}

export function isSupplierId(value: unknown): value is ISupplierID {
  if (typeof value !== 'string') {
    return false;
  }

  return /^(?:(?:EE|BB)[0-9]{6}|[A-Z]{2}[0-9]{8}|FS[0-9a-zA-Z_-]{7,14})$/.test(value);
}

export function isSupplierType(value: unknown): value is ISupplier_Type {
  if (typeof value !== 'string') {
    return false;
  }

  const types: ISupplier_Type[] = [
    'beauty',
    'cakes',
    'catering',
    'decoration',
    'dress',
    'entertainment',
    'florist',
    'group',
    'jewellery',
    'marquee',
    'menswear',
    'music',
    'photo',
    'planners',
    'stationery',
    'transport',
    'venue',
    'video',
  ];

  return types.includes(value as ISupplier_Type);
}

export function isURL(value: unknown): value is string {
  if (typeof value !== 'string') {
    return false;
  }

  try {
    new URL(value);
  } catch (error) {
    return false;
  }

  return /^https?:/i.test(value);
}

export function isUserId(value: unknown): value is IUserID {
  if (typeof value !== 'string') {
    return false;
  }

  return /^(?:[0-9a-zA-Z]{28}|[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}|(?:facebook|google):[0-9]+)$/i.test(
    value,
  );
}

/**
 * Checks if the given string is a valid UUIDv4.
 */
export function isUUID(value: unknown): value is string {
  if (typeof value !== 'string') {
    return false;
  }

  return /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(value);
}

/**
 * Checks if the given string is a valid UUIDv5.
 */
export function isUUIDv5(value: unknown): value is string {
  if (typeof value !== 'string') {
    return false;
  }

  return /^[0-9a-f]{8}-[0-9a-f]{4}-5[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(value);
}

export function isWeddingId(value: unknown): value is IWeddingID {
  if (typeof value !== 'string') {
    return false;
  }

  return /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12,13}$/.test(value);
}

/**
 * Checks if the given value is an object.
 */
export function isObject(value: unknown): value is Record<string, unknown> {
  return isDefined(value) && typeof value === 'object';
}
